import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    LayoutModule,
    SharedUiModule,
    TemplateDrawerComponent,
} from '@pf/shared-ui';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
    EquipmentDto,
    EquipmentFacade,
    InventoryBundleStatusOptions,
} from '@pf/platform-wms/domain';
import { asArraySafe } from '@pf/shared-utility';
import { containerHasAssignedBundles } from '@cpw/domain';
import { sumBy } from 'lodash-es';

export interface CpwEquipmentInfoDrawerData {
    equipment: EquipmentDto;
}

@Component({
    selector: 'cpw-equipment-info-drawer',
    imports: [SharedUiModule, LayoutModule],
    templateUrl: './cpw-equipment-info-drawer.component.html',
    styleUrl: './cpw-equipment-info-drawer.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwEquipmentInfoDrawerComponent
    implements TemplateDrawerComponent<CpwEquipmentInfoDrawerData>
{
    private readonly equipmentFacade = inject(EquipmentFacade);
    private readonly destroyRef = inject(DestroyRef);
    readonly data: CpwEquipmentInfoDrawerData = inject(NZ_DRAWER_DATA);
    drawerRef = inject(NzDrawerRef);

    readonly equipmentSignal = signal<EquipmentDto>(this.data.equipment);

    constructor() {
        const id = this.data.equipment?.id;
        if (id) {
            this.equipmentFacade
                .getById$(id, undefined, true)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(equipment => {
                    if (equipment) {
                        this.equipmentSignal.set(equipment);
                    }
                });
        }
    }

    containers = computed(() => {
        return asArraySafe(this.equipmentSignal().containers).map(container => {
            const bundles = asArraySafe(container.inventoryBundles).map(
                bundle => ({
                    itemName: bundle.item?.name || 'Unknown Item',
                    itemCode: bundle.item?.sku || bundle.item?.code || '',
                    totalQuantity: bundle.totalQuantity ?? 0,
                    status: bundle.status,
                    statusColor:
                        (
                            bundle.status &&
                            InventoryBundleStatusOptions[bundle.status]
                        )?.color ??
                        InventoryBundleStatusOptions.defaultOption.color,
                })
            );
            return {
                container,
                displayName:
                    container.lpn ||
                    container.containerType?.name ||
                    'Container',
                lpn: container.lpn || '',
                hasItems: containerHasAssignedBundles(container),
                bundles,
                totalQuantity: sumBy(bundles, b => b.totalQuantity),
            };
        });
    });
}
